import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCopyTradeStore } from '../stores/copyTradeStore';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import socketManager from '../lib/socket';
import TradeActivityLog from '../components/TradeActivityLog';
import EngineWarningsPanel from '../components/EngineWarningsPanel';
import {
  Plus,
  Settings,
  Trash2,
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Shield,
  DollarSign,
  Percent,
  Clock,
  Filter,
  Loader2,
  X,
  Copy,
  Wallet
} from 'lucide-react';

function ConfigCard({ config, onToggle, onEdit, onDelete, onDuplicate }) {
  const [expanded, setExpanded] = useState(false);
  const [isResettingBaseline, setIsResettingBaseline] = useState(false);
  const [isResettingStats, setIsResettingStats] = useState(false);

  const since = config.sinceActivation;
  const sinceTrades = since?.trades ?? null;
  const sinceWinRate = since?.winRate ?? null;
  const sincePnL = since?.realizedPnL ?? null;

  const handleResetBaseline = async () => {
    if (!config?._id) return;
    if (!confirm('Reset since-start baseline for this config?')) return;
    try {
      setIsResettingBaseline(true);
      await api.post(`/copy-trade/${config._id}/reset-baseline`);
      window.location.reload();
    } catch (_) {
      alert('Failed to reset baseline');
    } finally {
      setIsResettingBaseline(false);
    }
  };

  const handleResetStats = async () => {
    if (!config?._id) return;
    if (!confirm('Reset all performance stats (trades, win rate, P&L) for this config? This cannot be undone.')) return;
    try {
      setIsResettingStats(true);
      await api.post(`/copy-trade/${config._id}/reset-stats`);
      window.location.reload();
    } catch (_) {
      alert('Failed to reset stats');
    } finally {
      setIsResettingStats(false);
    }
  };
  
  return (
    <div className={`card ${config.isActive ? 'border-primary-500/50' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${config.isActive ? 'bg-emerald-400 animate-pulse' : 'bg-dark-500'}`} />
          <div>
            <h3 className="font-medium">{config.traderName}</h3>
            <p className="text-sm text-dark-400 font-mono">
              {config.traderAddress?.slice(0, 10)}...{config.traderAddress?.slice(-6)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggle(config._id)}
            className={`p-2 rounded-lg transition-colors ${
              config.isActive 
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
            }`}
          >
            {config.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(config)}
            className="p-2 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors"
            title="Edit"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDuplicate(config)}
            className="p-2 bg-dark-700 rounded-lg hover:bg-primary-500/20 hover:text-primary-400 transition-colors"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(config._id)}
            className="p-2 bg-dark-700 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-3 mt-4">
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Mode</p>
          <p className="text-sm font-medium capitalize">{config.mode || 'real'}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Max Size</p>
          <p className="text-sm font-medium">${config.sizing?.maxPositionSize}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Stop Loss</p>
          <p className="text-sm font-medium">
            {config.riskManagement?.stopLoss?.enabled 
              ? `${config.riskManagement.stopLoss.percentage || config.riskManagement.stopLoss.value}%` 
              : 'Off'}
          </p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Trades</p>
          <p className="text-sm font-medium">{config.stats?.totalCopiedTrades || 0}</p>
        </div>
      </div>

      {/* Expand/Collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-center gap-1 w-full mt-4 py-2 text-sm text-dark-400 hover:text-white transition-colors"
      >
        {expanded ? 'Hide Details' : 'Show Details'}
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-dark-700 space-y-4 animate-fade-in">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary-400" />
                Position Sizing
              </h4>
              <ul className="text-sm text-dark-400 space-y-1">
                <li>Mode: <span className="text-white capitalize">{config.sizing?.mode}</span></li>
                <li>Fixed Amount: <span className="text-white">${config.sizing?.fixedAmount}</span></li>
                <li>Max Position: <span className="text-white">${config.sizing?.maxPositionSize}</span></li>
                <li>Min Position: <span className="text-white">${config.sizing?.minPositionSize}</span></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary-400" />
                Risk Management
              </h4>
              <ul className="text-sm text-dark-400 space-y-1">
                <li>Stop Loss: <span className="text-white">
                  {config.riskManagement?.stopLoss?.enabled 
                    ? `${config.riskManagement.stopLoss.percentage || config.riskManagement.stopLoss.value}%`
                    : 'Disabled'}
                </span></li>
                <li>Take Profit: <span className="text-white">
                  {config.riskManagement?.takeProfit?.enabled 
                    ? `${config.riskManagement.takeProfit.percentage || config.riskManagement.takeProfit.value}%`
                    : 'Disabled'}
                </span></li>
                <li>Max Exposure: <span className="text-white">${config.riskManagement?.maxExposure}</span></li>
                <li>Daily Loss Limit: <span className="text-white">
                  {config.riskManagement?.dailyLossLimit?.enabled 
                    ? `$${config.riskManagement.dailyLossLimit.amount}` 
                    : 'Disabled'}
                </span></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Filter className="w-4 h-4 text-primary-400" />
                Filters
              </h4>
              <ul className="text-sm text-dark-400 space-y-1">
                <li>Copy Buys: <span className="text-white">{config.filters?.copyBuys ? 'Yes' : 'No'}</span></li>
                <li>Copy Sells: <span className="text-white">{config.filters?.copySells ? 'Yes' : 'No'}</span></li>
                <li>Min Trade Size: <span className="text-white">${config.filters?.minTradeSize}</span></li>
                <li>Price Range: <span className="text-white">{config.filters?.minPrice} - {config.filters?.maxPrice}</span></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary-400" />
                Execution
              </h4>
              <ul className="text-sm text-dark-400 space-y-1">
                <li>Order Type: <span className="text-white capitalize">{config.execution?.orderType}</span></li>
                <li>Slippage: <span className="text-white">{config.execution?.slippage}%</span></li>
                <li>Copy Delay: <span className="text-white">{config.execution?.copyDelay}s</span></li>
                <li>Retries: <span className="text-white">{config.execution?.retryAttempts}</span></li>
              </ul>
            </div>
          </div>

          {/* Stats */}
          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Performance</h4>
              <button
                onClick={handleResetStats}
                disabled={isResettingStats}
                className="text-xs px-2 py-1 rounded bg-dark-600 hover:bg-dark-500 text-dark-300 hover:text-white transition-colors"
              >
                {isResettingStats ? 'Resetting...' : 'Reset Stats'}
              </button>
            </div>
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-lg font-bold">{config.stats?.totalCopiedTrades || 0}</p>
                <p className="text-xs text-dark-400">Total Trades</p>
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-400">{config.stats?.successfulTrades || 0}</p>
                <p className="text-xs text-dark-400">Successful</p>
              </div>
              <div>
                <p className="text-lg font-bold text-red-400">{config.stats?.failedTrades || 0}</p>
                <p className="text-xs text-dark-400">Failed</p>
              </div>
              <div>
                <p className="text-lg font-bold">{(config.stats?.winRate || 0).toFixed(0)}%</p>
                <p className="text-xs text-dark-400">
                  Win Rate
                  {typeof config.stats?.winningTrades === 'number' && typeof config.stats?.losingTrades === 'number'
                    ? ` (${config.stats.winningTrades}W/${config.stats.losingTrades}L)`
                    : ''}
                </p>
              </div>
              <div>
                <p className={`text-lg font-bold ${(config.stats?.totalPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  ${(config.stats?.totalPnL || 0).toFixed(2)}
                </p>
                <p className="text-xs text-dark-400">Total P&L</p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Since Start</h4>
              <button
                onClick={handleResetBaseline}
                disabled={isResettingBaseline}
                className="btn btn-secondary"
              >
                {isResettingBaseline ? 'Resetting...' : 'Reset Baseline'}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-lg font-bold">{sinceTrades ?? 0}</p>
                <p className="text-xs text-dark-400">Trades</p>
              </div>
              <div>
                <p className="text-lg font-bold">{Number.isFinite(sinceWinRate) ? `${sinceWinRate.toFixed(0)}%` : '0%'}</p>
                <p className="text-xs text-dark-400">Win Rate</p>
              </div>
              <div>
                <p className={`text-lg font-bold ${(sincePnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  ${(sincePnL || 0).toFixed(2)}
                </p>
                <p className="text-xs text-dark-400">Realized P&L</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateConfigModal({ isOpen, onClose, initialTrader, onSubmit, editConfig = null, subscriptionTier = 'free' }) {
  const isEditMode = !!editConfig;
  const [mode, setMode] = useState('simple'); // 'simple' or 'advanced'
  
  const defaultFormData = {
    traderAddress: '',
    traderName: '',
    mode: 'real',
    // Position Sizing
    sizing: {
      mode: 'fixed', // 'fixed', 'percentage', 'proportional'
      fixedAmount: 10,
      percentageOfTrader: 10,
      percentageOfBalance: 5,
      maxPositionSize: 100,
      minPositionSize: 1, // Polymarket min is usually ~$1
      maxTradeSize: 0 // 0 = no limit
    },
    // Risk Management
    riskManagement: {
      stopLoss: {
        enabled: true,
        percentage: 25 // Exit if position drops 25%
      },
      takeProfit: {
        enabled: false,
        percentage: 50 // Exit if position gains 50%
      },
      maxExposure: 500, // Max total $ across all positions
      maxOpenPositions: 10,
      dailyLossLimit: {
        enabled: false,
        amount: 100
      }
    },
    // Market Filters
    filters: {
      copyBuys: true,
      copySells: true,
      marketCategory: 'all',
      includeKeywords: [],
      excludeKeywords: [],
      // Price range (0-1, where 0.50 = 50 cents)
      minPrice: 0.05, // Don't copy trades on outcomes below 5%
      maxPrice: 0.95, // Don't copy trades on outcomes above 95%
      // Min trader trade amount - only copy if trader's trade >= this value
      minTraderTradeAmount: 0, // 0 = copy any trade, e.g., 500 = only copy trades >= $500
      // Liquidity filters
      minLiquidity: 1000, // Min $ liquidity in order book
      minVolume24h: 0, // Min 24h volume
      // Market type filters
      excludeResolved: true,
      excludeNegRisk: false // Neg risk markets have different mechanics
    },
    // Auto-sell before market expiry
    autoSellBeforeExpiry: {
      enabled: false,
      minutesBefore: 30, // Sell 30 minutes before market expires
      sellPercentage: 100 // Sell 100% of position
    },
    // Execution Settings (Real Polymarket parameters)
    execution: {
      // Order type: MARKET, FOK, FAK, GTC, GTD
      orderType: 'MARKET', // Best for ensuring fills - matches database default
      // Price tolerance - separate limits for higher/lower prices
      priceTolerance: 0.02, // Legacy fallback (2 cents)
      maxPriceAbove: 0.10, // Max you'll pay ABOVE trader's price (10 cents - don't overpay much)
      maxPriceBelow: 1.00, // Max BELOW trader's price (1.00 = any lower price is fine)
      strictPriceTolerance: true, // Don't auto-widen tolerance based on trade age
      // For limit orders (GTC/GTD)
      limitOrderOffset: 0, // Place limit at same price as trader (0 = same, negative = better price)
      // GTD expiration
      gtdExpiration: 3600, // Seconds until GTD order expires (1 hour)
      // Timing
      copyDelay: 0, // Seconds to wait before copying (0 = instant)
      // Retry logic
      retryOnFail: true,
      maxRetries: 3,
      retryDelay: 2 // Seconds between retries
    },
    // Paper trading slippage simulation
    paperSlippage: {
      enabled: false,
      buySlippagePercent: 20,  // Assume 20% slippage on buys
      sellSlippagePercent: 10  // Assume 10% slippage on sells
    }
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editConfig) {
        // Editing existing config - populate with existing data
        setFormData({
          traderAddress: editConfig.traderAddress || '',
          traderName: editConfig.traderName || '',
          mode: editConfig.mode || 'real',
          sizing: { ...defaultFormData.sizing, ...editConfig.sizing },
          riskManagement: { 
            ...defaultFormData.riskManagement, 
            ...editConfig.riskManagement,
            stopLoss: { ...defaultFormData.riskManagement.stopLoss, ...editConfig.riskManagement?.stopLoss },
            takeProfit: { ...defaultFormData.riskManagement.takeProfit, ...editConfig.riskManagement?.takeProfit },
            dailyLossLimit: { ...defaultFormData.riskManagement.dailyLossLimit, ...editConfig.riskManagement?.dailyLossLimit }
          },
          filters: { ...defaultFormData.filters, ...editConfig.filters },
          autoSellBeforeExpiry: { ...defaultFormData.autoSellBeforeExpiry, ...editConfig.autoSellBeforeExpiry },
          execution: { ...defaultFormData.execution, ...editConfig.execution },
          paperSlippage: { ...defaultFormData.paperSlippage, ...editConfig.paperSlippage }
        });
      } else if (initialTrader) {
        // Creating new config with initial trader
        setFormData({
          ...defaultFormData,
          traderAddress: initialTrader.address
        });
      } else {
        // Creating new config from scratch
        setFormData(defaultFormData);
      }
    }
  }, [isOpen, editConfig, initialTrader]);

  useEffect(() => {
    if ((subscriptionTier || 'free') === 'free' && (formData.mode || 'real') === 'paper') {
      updateNested('mode', 'real');
    }
  }, [subscriptionTier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await onSubmit(formData);
    setIsLoading(false);
  };

  const updateNested = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Helper for number inputs - prevents leading zero issue
  const handleNumberInput = (path, e, defaultVal = 0, isInt = false) => {
    const raw = e.target.value;
    // Allow empty string while typing
    if (raw === '') {
      updateNested(path, '');
      return;
    }
    // Parse and update
    const parsed = isInt ? parseInt(raw, 10) : parseFloat(raw);
    updateNested(path, Number.isFinite(parsed) ? parsed : defaultVal);
  };

  // Get value for number input - show empty string if value is empty string
  const getNumberValue = (value, defaultVal = 0) => {
    if (value === '' || value === null || value === undefined) return '';
    return value;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-dark-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-dark-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Header with Mode Toggle */}
        <div className="p-6 border-b border-dark-700">
          <div className="flex items-center justify-between mb-2 pr-10">
            <h2 className="text-xl font-bold">{isEditMode ? 'Edit Copy Configuration' : 'Create Copy Configuration'}</h2>
            <div className="flex bg-dark-700 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setMode('simple')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  mode === 'simple' ? 'bg-primary-600 text-white' : 'text-dark-400 hover:text-white'
                }`}
              >
                Simple
              </button>
              <button
                type="button"
                onClick={() => setMode('advanced')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  mode === 'advanced' ? 'bg-primary-600 text-white' : 'text-dark-400 hover:text-white'
                }`}
              >
                Advanced
              </button>
            </div>
          </div>
          <p className="text-dark-400 text-sm">
            {mode === 'simple' 
              ? 'Quick setup with recommended defaults' 
              : 'Full control over all trading parameters'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Trader Info - Always shown */}
          <div className="space-y-4">
            <h3 className="font-medium">Trader to Copy</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Wallet Address *</label>
                <input
                  type="text"
                  value={formData.traderAddress}
                  onChange={(e) => updateNested('traderAddress', e.target.value)}
                  className="input font-mono text-sm"
                  placeholder="0x..."
                  required
                />
              </div>
              <div>
                <label className="label">Nickname (optional)</label>
                <input
                  type="text"
                  value={formData.traderName}
                  onChange={(e) => updateNested('traderName', e.target.value)}
                  className="input"
                  placeholder="e.g., Whale Trader"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium">Trading Mode</h3>
            <div className="flex bg-dark-700/50 rounded-xl p-1 border border-dark-600/50">
              <button
                type="button"
                onClick={() => updateNested('mode', 'real')}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                  (formData.mode || 'real') === 'real'
                    ? 'bg-primary-600 text-white'
                    : 'text-dark-400 hover:text-white'
                }`}
              >
                Real
              </button>
              <button
                type="button"
                onClick={() => {
                  if ((subscriptionTier || 'free') === 'free') return;
                  updateNested('mode', 'paper');
                }}
                disabled={(subscriptionTier || 'free') === 'free'}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                  (subscriptionTier || 'free') === 'free'
                    ? 'text-dark-500 opacity-60 cursor-not-allowed'
                    : (formData.mode || 'real') === 'paper'
                      ? 'bg-primary-600 text-white'
                      : 'text-dark-400 hover:text-white'
                }`}
              >
                Paper
              </button>
            </div>
          </div>

          {/* === SIMPLE MODE === */}
          {mode === 'simple' && (
            <>
              {/* Position Size */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary-400" />
                  How much to copy?
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Amount per trade ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.sizing.fixedAmount)}
                      onChange={(e) => handleNumberInput('sizing.fixedAmount', e, 0)}
                      className="input"
                      min="1"
                      step="1"
                    />
                    <p className="text-xs text-dark-500 mt-1">Fixed $ amount for each copied trade</p>
                  </div>
                  <div>
                    <label className="label">Max total exposure ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.riskManagement.maxExposure)}
                      onChange={(e) => handleNumberInput('riskManagement.maxExposure', e, 0)}
                      className="input"
                      min="1"
                    />
                    <p className="text-xs text-dark-500 mt-1">Stop copying when you have this much at risk</p>
                  </div>
                </div>
              </div>

              {/* Simple Risk */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary-400" />
                  Risk Protection
                </h3>
                <div className="p-4 bg-dark-700/50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Auto Stop-Loss</p>
                      <p className="text-xs text-dark-400">Exit positions that drop too much</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.riskManagement.stopLoss.enabled}
                        onChange={(e) => updateNested('riskManagement.stopLoss.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  {formData.riskManagement.stopLoss.enabled && (
                    <div>
                      <label className="label">Exit if position drops by (%)</label>
                      <input
                        type="number"
                        value={getNumberValue(formData.riskManagement.stopLoss.percentage)}
                        onChange={(e) => handleNumberInput('riskManagement.stopLoss.percentage', e, 0)}
                        className="input"
                        min="5"
                        max="90"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* What to Copy */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-400" />
                  What to copy?
                </h3>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.copyBuys}
                      onChange={(e) => updateNested('filters.copyBuys', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span>Copy Buys</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.copySells}
                      onChange={(e) => updateNested('filters.copySells', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span>Copy Sells</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* === ADVANCED MODE === */}
          {mode === 'advanced' && (
            <>
              {/* Position Sizing */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary-400" />
                  Position Sizing
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Sizing Mode</label>
                    <select
                      value={formData.sizing.mode}
                      onChange={(e) => updateNested('sizing.mode', e.target.value)}
                      className="input"
                    >
                      <option value="fixed">Fixed Amount ($)</option>
                      <option value="percentage">% of Trader's Size</option>
                      <option value="proportional">% of Your Balance</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">
                      {formData.sizing.mode === 'fixed' ? 'Amount ($)' :
                       formData.sizing.mode === 'percentage' ? 'Percentage (%)' : 'Balance (%)'}
                    </label>
                    <input
                      type="number"
                      value={getNumberValue(formData.sizing.mode === 'fixed' ? formData.sizing.fixedAmount :
                             formData.sizing.mode === 'percentage' ? formData.sizing.percentageOfTrader :
                             formData.sizing.percentageOfBalance)}
                      onChange={(e) => {
                        const key = formData.sizing.mode === 'fixed' ? 'sizing.fixedAmount' :
                                   formData.sizing.mode === 'percentage' ? 'sizing.percentageOfTrader' :
                                   'sizing.percentageOfBalance';
                        handleNumberInput(key, e, 0);
                      }}
                      className="input"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="label">Min Position ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.sizing.minPositionSize)}
                      onChange={(e) => handleNumberInput('sizing.minPositionSize', e, 1)}
                      className="input"
                      min="1"
                      step="0.1"
                    />
                    <p className="text-xs text-dark-500 mt-1">Polymarket min is ~$1</p>
                  </div>
                  <div>
                    <label className="label">Max Position Size ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.sizing.maxPositionSize)}
                      onChange={(e) => handleNumberInput('sizing.maxPositionSize', e, 100)}
                      className="input"
                      min="1"
                    />
                    <p className="text-xs text-dark-500 mt-1">Max $ you can accumulate in one market</p>
                  </div>
                  {/* Only show Max Trade Size for %-based modes (not fixed amount) */}
                  {formData.sizing.mode !== 'fixed' && (
                    <div>
                      <label className="label">Max Trade Size ($)</label>
                      <input
                        type="number"
                        value={getNumberValue(formData.sizing.maxTradeSize)}
                        onChange={(e) => handleNumberInput('sizing.maxTradeSize', e, 0)}
                        className="input"
                        min="0"
                      />
                      <p className="text-xs text-dark-500 mt-1">Cap each individual buy (0 = no limit)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Risk Management */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary-400" />
                  Risk Management
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Max Total Exposure ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.riskManagement.maxExposure)}
                      onChange={(e) => handleNumberInput('riskManagement.maxExposure', e, 0)}
                      className="input"
                      min="0"
                    />
                    <p className="text-xs text-dark-500 mt-1">Max $ across ALL positions for this trader</p>
                  </div>
                  <div>
                    <label className="label">Max Open Positions (#)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.riskManagement.maxOpenPositions)}
                      onChange={(e) => handleNumberInput('riskManagement.maxOpenPositions', e, 1, true)}
                      className="input"
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                {/* Stop Loss */}
                <div className="p-4 bg-dark-700/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Stop Loss</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.riskManagement.stopLoss.enabled}
                        onChange={(e) => updateNested('riskManagement.stopLoss.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  {formData.riskManagement.stopLoss.enabled && (
                    <div>
                      <label className="label">Stop Loss (%)</label>
                      <input
                        type="number"
                        value={getNumberValue(formData.riskManagement.stopLoss.percentage)}
                        onChange={(e) => handleNumberInput('riskManagement.stopLoss.percentage', e, 0)}
                        className="input"
                        min="1"
                        max="99"
                      />
                    </div>
                  )}
                </div>

                {/* Take Profit */}
                <div className="p-4 bg-dark-700/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Take Profit</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.riskManagement.takeProfit.enabled}
                        onChange={(e) => updateNested('riskManagement.takeProfit.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  {formData.riskManagement.takeProfit.enabled && (
                    <div>
                      <label className="label">Take Profit (%)</label>
                      <input
                        type="number"
                        value={getNumberValue(formData.riskManagement.takeProfit.percentage)}
                        onChange={(e) => handleNumberInput('riskManagement.takeProfit.percentage', e, 0)}
                        className="input"
                        min="1"
                      />
                    </div>
                  )}
                </div>

                {/* Daily Loss Limit */}
                <div className="p-4 bg-dark-700/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Daily Loss Limit</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.riskManagement.dailyLossLimit.enabled}
                        onChange={(e) => updateNested('riskManagement.dailyLossLimit.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  {formData.riskManagement.dailyLossLimit.enabled && (
                    <div>
                      <label className="label">Max Daily Loss ($)</label>
                      <input
                        type="number"
                        value={getNumberValue(formData.riskManagement.dailyLossLimit.amount)}
                        onChange={(e) => handleNumberInput('riskManagement.dailyLossLimit.amount', e, 0)}
                        className="input"
                        min="0"
                      />
                    </div>
                  )}
                </div>

                {/* Auto-Sell Before Expiry */}
                <div className="p-4 bg-dark-700/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">Auto-Sell Before Expiry</span>
                      <p className="text-xs text-dark-400">Automatically sell positions before market closes</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.autoSellBeforeExpiry?.enabled || false}
                        onChange={(e) => updateNested('autoSellBeforeExpiry.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  {formData.autoSellBeforeExpiry?.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Minutes Before Expiry</label>
                        <input
                          type="number"
                          value={getNumberValue(formData.autoSellBeforeExpiry?.minutesBefore, 30)}
                          onChange={(e) => handleNumberInput('autoSellBeforeExpiry.minutesBefore', e, 30, true)}
                          className="input"
                          min="1"
                          max="1440"
                        />
                        <p className="text-xs text-dark-500 mt-1">Sell X minutes before market expires</p>
                      </div>
                      <div>
                        <label className="label">Sell Percentage (%)</label>
                        <input
                          type="number"
                          value={getNumberValue(formData.autoSellBeforeExpiry?.sellPercentage, 100)}
                          onChange={(e) => handleNumberInput('autoSellBeforeExpiry.sellPercentage', e, 100, true)}
                          className="input"
                          min="1"
                          max="100"
                        />
                        <p className="text-xs text-dark-500 mt-1">100% = sell entire position</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Paper Slippage Simulation (only for paper mode) */}
                {formData.mode === 'paper' && (
                  <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm text-amber-300">Paper Slippage Simulation</span>
                        <p className="text-xs text-dark-400">Simulate realistic slippage for backtesting analysis</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.paperSlippage?.enabled || false}
                          onChange={(e) => updateNested('paperSlippage.enabled', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-dark-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                      </label>
                    </div>
                    {formData.paperSlippage?.enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="label">Buy Slippage (%)</label>
                          <input
                            type="number"
                            value={getNumberValue(formData.paperSlippage?.buySlippagePercent, 20)}
                            onChange={(e) => handleNumberInput('paperSlippage.buySlippagePercent', e, 20, true)}
                            className="input"
                            min="0"
                            max="50"
                          />
                          <p className="text-xs text-dark-500 mt-1">Price increases by this % on buys</p>
                        </div>
                        <div>
                          <label className="label">Sell Slippage (%)</label>
                          <input
                            type="number"
                            value={getNumberValue(formData.paperSlippage?.sellSlippagePercent, 10)}
                            onChange={(e) => handleNumberInput('paperSlippage.sellSlippagePercent', e, 10, true)}
                            className="input"
                            min="0"
                            max="50"
                          />
                          <p className="text-xs text-dark-500 mt-1">Price decreases by this % on sells</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Market Filters */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-400" />
                  Market Filters
                </h3>
                
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.copyBuys}
                      onChange={(e) => updateNested('filters.copyBuys', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span>Copy Buys</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.copySells}
                      onChange={(e) => updateNested('filters.copySells', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span>Copy Sells</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Copy specific category only</label>
                    <select
                      value={formData.filters.marketCategory || 'all'}
                      onChange={(e) => updateNested('filters.marketCategory', e.target.value)}
                      className="input"
                    >
                      <option value="all">All markets</option>
                      <option value="sports">Sports only</option>
                      <option value="politics">Politics only</option>
                      <option value="crypto">Crypto only</option>
                      <option value="other">Other (non-sports, non-politics, non-crypto)</option>
                    </select>
                    <p className="text-xs text-dark-500 mt-1">Restricts copying to this market type</p>
                  </div>
                  <div>
                    <label className="label">Keyword match (optional)</label>
                    <input
                      type="text"
                      value={(Array.isArray(formData.filters.includeKeywords) ? formData.filters.includeKeywords.join(', ') : '')}
                      onChange={(e) => {
                        const parts = e.target.value
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                          .slice(0, 30);
                        updateNested('filters.includeKeywords', parts);
                      }}
                      className="input"
                      placeholder="e.g. nfl, super bowl, trump"
                    />
                    <p className="text-xs text-dark-500 mt-1">Only copy if title, slug, or tags contain any of these (comma-separated). Tags like NFL, NBA, Soccer work!</p>
                  </div>
                  <div>
                    <label className="label">Min Trader Trade Amount ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.filters.minTraderTradeAmount)}
                      onChange={(e) => handleNumberInput('filters.minTraderTradeAmount', e, 0)}
                      className="input"
                      min="0"
                      step="10"
                    />
                    <p className="text-xs text-dark-500 mt-1">Only copy if trader's trade is ≥ this amount (0 = any)</p>
                  </div>
                  <div>
                    <label className="label">Min Price (0-1)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.filters.minPrice)}
                      onChange={(e) => handleNumberInput('filters.minPrice', e, 0)}
                      className="input"
                      min="0"
                      max="1"
                      step="0.01"
                    />
                    <p className="text-xs text-dark-500 mt-1">Skip outcomes below this probability</p>
                  </div>
                  <div>
                    <label className="label">Max Price (0-1)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.filters.maxPrice)}
                      onChange={(e) => handleNumberInput('filters.maxPrice', e, 1)}
                      className="input"
                      min="0"
                      max="1"
                      step="0.01"
                    />
                    <p className="text-xs text-dark-500 mt-1">Skip outcomes above this probability</p>
                  </div>
                  <div>
                    <label className="label">Min Liquidity ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.filters.minLiquidity)}
                      onChange={(e) => handleNumberInput('filters.minLiquidity', e, 0)}
                      className="input"
                      min="0"
                    />
                    <p className="text-xs text-dark-500 mt-1">Order book depth required</p>
                  </div>
                  <div>
                    <label className="label">Min 24h Volume ($)</label>
                    <input
                      type="number"
                      value={getNumberValue(formData.filters.minVolume24h)}
                      onChange={(e) => handleNumberInput('filters.minVolume24h', e, 0)}
                      className="input"
                      min="0"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Exclude keywords (optional)</label>
                    <input
                      type="text"
                      value={(Array.isArray(formData.filters.excludeKeywords) ? formData.filters.excludeKeywords.join(', ') : '')}
                      onChange={(e) => {
                        const parts = e.target.value
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                          .slice(0, 30);
                        updateNested('filters.excludeKeywords', parts);
                      }}
                      className="input"
                      placeholder="e.g. updown, 15m"
                    />
                    <p className="text-xs text-dark-500 mt-1">Never copy if title, slug, or tags contain any of these (comma-separated)</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.excludeResolved}
                      onChange={(e) => updateNested('filters.excludeResolved', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span className="text-sm">Skip resolved markets</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.filters.excludeNegRisk}
                      onChange={(e) => updateNested('filters.excludeNegRisk', e.target.checked)}
                      className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600"
                    />
                    <span className="text-sm">Skip neg-risk markets</span>
                  </label>
                </div>
              </div>

              {/* Execution Settings */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary-400" />
                  Order Execution
                </h3>
                
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4">
                  <p className="text-sm text-amber-200">
                    <strong>Note:</strong> Polymarket uses a limit order book. There's no traditional "slippage" - 
                    you set a price and it either fills or doesn't.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Order Type</label>
                    <select
                      value={formData.execution.orderType}
                      onChange={(e) => updateNested('execution.orderType', e.target.value)}
                      className="input"
                    >
                      <option value="MARKET">MARKET (Aggressive fill)</option>
                      <option value="FOK">FOK (Fill-or-Kill)</option>
                      <option value="FAK">FAK (Fill-and-Kill)</option>
                      <option value="GTC">GTC (Good-til-Cancelled)</option>
                      <option value="GTD">GTD (Good-til-Date)</option>
                    </select>
                    <p className="text-xs text-dark-500 mt-1">
                      {formData.execution.orderType === 'MARKET' && 'Best for ensuring fills - uses aggressive pricing'}
                      {formData.execution.orderType === 'FOK' && 'Must fill entirely or cancel'}
                      {formData.execution.orderType === 'FAK' && 'Fill what you can, cancel rest'}
                      {formData.execution.orderType === 'GTC' && 'Stays open until filled/cancelled'}
                      {formData.execution.orderType === 'GTD' && 'Expires after set time'}
                    </p>
                  </div>
                  <div>
                    <label className="label">Max Price Above Trader ($)</label>
                    <input
                      type="number"
                      value={formData.execution.maxPriceAbove ?? formData.execution.priceTolerance ?? 0.02}
                      onChange={(e) => updateNested('execution.maxPriceAbove', parseFloat(e.target.value) || 0)}
                      className="input"
                      min="0"
                      max="0.50"
                      step="0.01"
                    />
                    <p className="text-xs text-dark-500 mt-1">Max you'll pay ABOVE trader's price (e.g., 0.10 = 10¢ max overpay)</p>
                  </div>
                  <div>
                    <label className="label">Max Price Below Trader ($)</label>
                    <input
                      type="number"
                      value={formData.execution.maxPriceBelow ?? formData.execution.priceTolerance ?? 0.02}
                      onChange={(e) => updateNested('execution.maxPriceBelow', parseFloat(e.target.value) || 0)}
                      className="input"
                      min="0"
                      max="1.00"
                      step="0.01"
                    />
                    <p className="text-xs text-dark-500 mt-1">Max BELOW trader's price you'll accept (e.g., 1.00 = any lower is fine)</p>
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.execution.strictPriceTolerance !== false}
                        onChange={(e) => updateNested('execution.strictPriceTolerance', e.target.checked)}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium">Strict Price Tolerance</span>
                        <p className="text-xs text-dark-500">
                          {formData.execution.strictPriceTolerance !== false 
                            ? "✓ Enforces exact tolerance - trades rejected if price moves beyond limit"
                            : "⚠️ Tolerance auto-widens for older trades (up to 10¢ after 60s)"}
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  {(formData.execution.orderType === 'GTC' || formData.execution.orderType === 'GTD') && (
                    <div>
                      <label className="label">Limit Order Offset ($)</label>
                      <input
                        type="number"
                        value={formData.execution.limitOrderOffset}
                        onChange={(e) => updateNested('execution.limitOrderOffset', parseFloat(e.target.value) || 0)}
                        className="input"
                        min="-0.10"
                        max="0.10"
                        step="0.01"
                      />
                      <p className="text-xs text-dark-500 mt-1">0 = same price, negative = better price</p>
                    </div>
                  )}
                  
                  {formData.execution.orderType === 'GTD' && (
                    <div>
                      <label className="label">GTD Expiration (seconds)</label>
                      <input
                        type="number"
                        value={formData.execution.gtdExpiration}
                        onChange={(e) => updateNested('execution.gtdExpiration', parseInt(e.target.value) || 3600)}
                        className="input"
                        min="60"
                      />
                      <p className="text-xs text-dark-500 mt-1">3600 = 1 hour</p>
                    </div>
                  )}

                  <div>
                    <label className="label">Copy Delay (seconds)</label>
                    <input
                      type="number"
                      value={formData.execution.copyDelay}
                      onChange={(e) => updateNested('execution.copyDelay', parseInt(e.target.value) || 0)}
                      className="input"
                      min="0"
                    />
                    <p className="text-xs text-dark-500 mt-1">0 = copy instantly</p>
                  </div>
                  <div>
                    <label className="label">Max Retries</label>
                    <input
                      type="number"
                      value={formData.execution.maxRetries}
                      onChange={(e) => updateNested('execution.maxRetries', parseInt(e.target.value) || 0)}
                      className="input"
                      min="0"
                      max="10"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.traderAddress}
              className="btn btn-primary"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                isEditMode ? 'Save Changes' : 'Create Configuration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CopyConfigs() {
  const location = useLocation();
  const { configs, isLoading, fetchConfigs, createConfig, updateConfig, toggleConfig, deleteConfig } = useCopyTradeStore();
  const { user, refreshUser } = useAuthStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [initialTrader, setInitialTrader] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('free');

  const activityCacheKey = 'betmimic.copyActivity.cache';
  const activityFilterKey = 'betmimic.copyActivity.filters';

  const [activityEvents, setActivityEvents] = useState(() => {
    try {
      const raw = localStorage.getItem(activityCacheKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [activityConfigId, setActivityConfigId] = useState(() => {
    try {
      const raw = localStorage.getItem(activityFilterKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.configId ? parsed.configId.toString() : '';
    } catch (_) {
      return '';
    }
  });
  const [activityTraderAddress, setActivityTraderAddress] = useState(() => {
    try {
      const raw = localStorage.getItem(activityFilterKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.traderAddress ? parsed.traderAddress.toString() : '';
    } catch (_) {
      return '';
    }
  });
  const [activityIncludeInferred, setActivityIncludeInferred] = useState(() => {
    try {
      const raw = localStorage.getItem(activityFilterKey);
      const parsed = raw ? JSON.parse(raw) : null;
      return !!parsed?.includeInferred;
    } catch (_) {
      return false;
    }
  });


  useEffect(() => {
    fetchConfigs();
    refreshUser(); // Refresh user data to get latest wallet balance

    let canceled = false;
    const fetchSubscription = async () => {
      try {
        const res = await api.get('/payments/subscription');
        if (!canceled) {
          setSubscriptionTier(res.data?.effectiveTier || res.data?.tier || 'free');
        }
      } catch (e) {
        console.error('Error fetching subscription:', e);
        if (!canceled) {
          setSubscriptionTier('free');
        }
      }
    };
    fetchSubscription();
    
    // Check if we came from traders page with a trader to copy
    if (location.state?.trader) {
      setInitialTrader(location.state.trader);
      setShowCreateModal(true);
    }
    return () => {
      canceled = true;
    };
  }, []);

  const fetchActivity = async ({ limit = 150 } = {}) => {
    setActivityError('');
    setActivityLoading(true);
    try {
      const params = {
        limit,
        ...(activityConfigId ? { configId: activityConfigId } : {}),
        ...(activityTraderAddress ? { traderAddress: activityTraderAddress } : {})
      };
      const res = await api.get('/copy-trade/activity', { params });
      setActivityEvents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setActivityError(e.response?.data?.error || 'Failed to load activity');
    } finally {
      setActivityLoading(false);
    }
  };
  
  const syncPositions = async () => {
    setIsSyncing(true);
    setActivityError('');
    try {
      const res = await api.post('/copy-trade/sync-positions', { configId: activityConfigId || null });
      const { synced, skipped, errors } = res.data;
      if (synced > 0) {
        setActivityError(`✅ Synced ${synced} position(s) from Polymarket with SL/TP applied`);
      } else if (skipped > 0) {
        setActivityError(`ℹ️ All ${skipped} positions already in sync`);
      } else {
        setActivityError('ℹ️ No new positions found to sync');
      }
      fetchActivity();
    } catch (e) {
      setActivityError('Failed to sync: ' + (e.response?.data?.error || e.message));
    } finally {
      setIsSyncing(false);
    }
  };


  useEffect(() => {
    if (!user?._id) return;
    socketManager.subscribeToPositions(user._id);

    const onCopyActivity = (evt) => {
      if (!evt) return;
      setActivityEvents((prev) => {
        const existing = prev || [];
        const id = evt?.id;
        if (id && existing.some((e) => e?.id === id)) {
          return existing;
        }
        return [evt, ...existing].slice(0, 250);
      });
    };

    socketManager.on('copyActivity', onCopyActivity);

    return () => {
      socketManager.off('copyActivity', onCopyActivity);
    };
  }, [user?._id]);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          activityFilterKey,
          JSON.stringify({
            configId: activityConfigId || '',
            traderAddress: activityTraderAddress || '',
            includeInferred: !!activityIncludeInferred
          })
        );
      } catch (_) {
      }
    }, 150);
    return () => clearTimeout(t);
  }, [activityConfigId, activityTraderAddress, activityIncludeInferred]);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const trimmed = Array.isArray(activityEvents) ? activityEvents.slice(0, 250) : [];
        localStorage.setItem(activityCacheKey, JSON.stringify(trimmed));
      } catch (_) {
      }
    }, 200);
    return () => clearTimeout(t);
  }, [activityEvents]);

  useEffect(() => {
    if (!subscriptionTier) return;
    fetchActivity();
  }, [subscriptionTier]);

  useEffect(() => {
    if (!subscriptionTier) return;
    const t = setTimeout(() => {
      fetchActivity();
    }, 300);
    return () => clearTimeout(t);
  }, [subscriptionTier, activityConfigId, activityTraderAddress]);

  const visibleActivity = useMemo(() => {
    const cfg = activityConfigId ? activityConfigId.toString() : '';
    const trader = activityTraderAddress ? activityTraderAddress.toString().toLowerCase() : '';
    return (activityEvents || []).filter((e) => {
      if (cfg && (e?.configId || '').toString() !== cfg) return false;
      if (trader && (e?.traderAddress || '').toString().toLowerCase() !== trader) return false;
      const inferred = e?.trade?.inferred === true || (typeof e?.trade?.transactionHash === 'string' && e.trade.transactionHash.startsWith('posdelta:'));
      if (!activityIncludeInferred && inferred) return false;
      return true;
    });
  }, [activityEvents, activityConfigId, activityTraderAddress, activityIncludeInferred]);

  const activityCounts = useMemo(() => {
    const out = {
      observed: 0,
      filtered: 0,
      queued: 0,
      executed: 0,
      failed: 0,
      reconcile: 0
    };
    for (const e of visibleActivity || []) {
      const k = (e?.kind || '').toString();
      if (k === 'trade_observed') out.observed += 1;
      else if (k === 'trade_filtered') out.filtered += 1;
      else if (k === 'trade_queued') out.queued += 1;
      else if (k === 'trade_executed') out.executed += 1;
      else if (k === 'trade_failed') out.failed += 1;
      else if (k === 'reconcile_diff') out.reconcile += 1;
    }
    return out;
  }, [visibleActivity]);

  // Aggregate performance stats across all configs
  const aggregateStats = useMemo(() => {
    let totalTrades = 0;
    let successfulTrades = 0;
    let failedTrades = 0;
    let winningTrades = 0;
    let losingTrades = 0;
    let totalPnL = 0;
    let activeConfigs = 0;

    for (const cfg of configs || []) {
      const stats = cfg.stats || {};
      totalTrades += stats.totalCopiedTrades || 0;
      successfulTrades += stats.successfulTrades || 0;
      failedTrades += stats.failedTrades || 0;
      winningTrades += stats.winningTrades || 0;
      losingTrades += stats.losingTrades || 0;
      totalPnL += stats.totalPnL || 0;
      if (cfg.isActive) activeConfigs += 1;
    }

    const winRate = (winningTrades + losingTrades) > 0 
      ? (winningTrades / (winningTrades + losingTrades)) * 100 
      : 0;

    return {
      totalTrades,
      successfulTrades,
      failedTrades,
      winningTrades,
      losingTrades,
      totalPnL,
      winRate,
      activeConfigs,
      totalConfigs: configs?.length || 0
    };
  }, [configs]);

  const handleCreate = async (formData) => {
    const result = await createConfig(formData);
    if (result.success) {
      setShowCreateModal(false);
      setInitialTrader(null);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowEditModal(true);
  };

  const handleUpdate = async (formData) => {
    // If editingConfig has no _id, this is a duplicate - create new instead of update
    let result;
    if (editingConfig._id) {
      result = await updateConfig(editingConfig._id, formData);
    } else {
      // This is a duplicated config, create a new one
      result = await createConfig(formData);
    }
    if (result.success) {
      setShowEditModal(false);
      setEditingConfig(null);
    }
  };

  const handleToggle = async (id) => {
    await toggleConfig(id);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this configuration?')) {
      await deleteConfig(id);
    }
  };

  const handleDuplicate = (config) => {
    // Create a copy of the config with modified name
    const duplicatedConfig = {
      ...config,
      traderName: config.traderName ? `${config.traderName} (Copy)` : 'Copy',
      isActive: false
    };
    // Remove fields that shouldn't be duplicated
    delete duplicatedConfig._id;
    delete duplicatedConfig.createdAt;
    delete duplicatedConfig.updatedAt;
    delete duplicatedConfig.sinceActivation;
    
    // Open edit modal with pre-filled data (will create new on save)
    setEditingConfig(duplicatedConfig);
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Wallet Balance */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Copy Configurations</h1>
          <p className="text-dark-400">Manage your automated copy trading settings</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Wallet Balance Display */}
          {user?.wallet?.address && (
            <div className="flex items-center gap-2 px-4 py-2 bg-dark-800 rounded-lg border border-dark-700">
              <Wallet className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-dark-400">Balance:</span>
              <span className="text-sm font-bold text-white">
                ${(user?.wallet?.balance || 0).toFixed(2)}
              </span>
            </div>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Config
          </button>
        </div>
      </div>

      {/* Aggregate Performance Summary */}
      {configs.length > 0 && (
        <div className="card bg-gradient-to-r from-dark-800 to-dark-800/80 border-primary-500/20">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Copy Trading Performance</h2>
            <div className="flex items-center gap-2 text-sm">
              <span className={`px-2 py-1 rounded-lg ${aggregateStats.activeConfigs > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-dark-700 text-dark-400'}`}>
                {aggregateStats.activeConfigs}/{aggregateStats.totalConfigs} Active
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-dark-700/50 rounded-xl">
              <p className="text-2xl font-bold">{aggregateStats.totalTrades}</p>
              <p className="text-xs text-dark-400 mt-1">Total Trades</p>
            </div>
            <div className="text-center p-3 bg-dark-700/50 rounded-xl">
              <p className="text-2xl font-bold text-emerald-400">{aggregateStats.successfulTrades}</p>
              <p className="text-xs text-dark-400 mt-1">Successful</p>
            </div>
            <div className="text-center p-3 bg-dark-700/50 rounded-xl">
              <p className="text-2xl font-bold">{aggregateStats.winRate.toFixed(0)}%</p>
              <p className="text-xs text-dark-400 mt-1">Win Rate ({aggregateStats.winningTrades}W/{aggregateStats.losingTrades}L)</p>
            </div>
            <div className="text-center p-3 bg-dark-700/50 rounded-xl">
              <p className={`text-2xl font-bold ${aggregateStats.totalPnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {aggregateStats.totalPnL >= 0 ? '+' : ''}${aggregateStats.totalPnL.toFixed(2)}
              </p>
              <p className="text-xs text-dark-400 mt-1">Total P&L</p>
            </div>
            <div className="text-center p-3 bg-dark-700/50 rounded-xl">
              <p className="text-2xl font-bold text-red-400">{aggregateStats.failedTrades}</p>
              <p className="text-xs text-dark-400 mt-1">Failed</p>
            </div>
          </div>

        </div>
      )}

      {/* Engine Warnings Panel */}
      <EngineWarningsPanel maxVisible={4} />

      <div className="card">
        <div>
          <h2 className="text-lg font-semibold">Copy Trading Activity</h2>
          <p className="text-sm text-dark-400 mt-1">
            Live readout of observed trades, filter decisions, execution outcomes, and reconciliation diffs.
          </p>
        </div>

        {activityError ? (
          <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {activityError}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col md:flex-row md:flex-wrap md:items-end gap-3">
          <button
            onClick={() => fetchActivity()}
            disabled={activityLoading}
            className="btn btn-secondary"
            type="button"
          >
            {activityLoading ? 'Loading…' : 'Refresh'}
          </button>
          
          <button
            onClick={syncPositions}
            disabled={isSyncing}
            className="px-3 py-2 text-sm rounded-lg bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30 transition-colors border border-cyan-500/30"
            type="button"
            title="Sync positions from Polymarket - recovers any missing positions with SL/TP"
          >
            {isSyncing ? '🔄 Syncing...' : '🔄 Sync Positions'}
          </button>
          
          <button
            onClick={() => {
              if (confirm('Clear cached activity data? This will remove stale/old activity from local storage.')) {
                localStorage.removeItem('betmimic.copyActivity.cache');
                setActivityEvents([]);
                fetchActivity();
              }
            }}
            className="px-3 py-2 text-sm rounded-lg bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white transition-colors"
            type="button"
            title="Clear cached activity data"
          >
            🗑️ Clear Cache
          </button>

          <div className="w-full md:w-72">
            <p className="text-xs text-dark-500">Filter by config</p>
            <select
              value={activityConfigId}
              onChange={(e) => setActivityConfigId(e.target.value)}
              className="input"
            >
              <option value="">All configs</option>
              {configs.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.traderName || c.traderAddress}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full md:w-72">
            <p className="text-xs text-dark-500">Filter by trader address</p>
            <input
              value={activityTraderAddress}
              onChange={(e) => setActivityTraderAddress(e.target.value)}
              className="input"
              placeholder="0x…"
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-dark-400 select-none">
              <input
                type="checkbox"
                checked={activityIncludeInferred}
                onChange={(e) => setActivityIncludeInferred(e.target.checked)}
              />
              Include inferred position-delta trades
            </label>
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto">
            <button
              onClick={() => {
                setActivityConfigId('');
                setActivityTraderAddress('');
              }}
              className="btn btn-secondary"
              type="button"
            >
              Clear
            </button>
            <div className="flex-1 md:min-w-[320px] p-3 rounded-xl bg-dark-800/60 border border-dark-700">
              <p className="text-xs text-dark-500">Last {Math.min(visibleActivity.length, 250)} events</p>
              <p className="text-xs text-dark-300 mt-1">
                Obs {activityCounts.observed} •
                Filt {activityCounts.filtered} •
                Queue {activityCounts.queued} •
                Exec {activityCounts.executed} •
                Fail {activityCounts.failed} •
                Recon {activityCounts.reconcile}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 max-h-[420px] overflow-auto rounded-xl border border-dark-700">
          {visibleActivity.length === 0 ? (
            <div className="p-4 text-sm text-dark-400">
              No activity yet. Once your copied trader makes trades, you’ll see every decision here.
            </div>
          ) : (
            <div className="divide-y divide-dark-700">
              {visibleActivity.slice(0, 250).map((e) => {
                const kind = (e?.kind || '').toString();
                const createdAt = e?.createdAt ? new Date(e.createdAt).toLocaleString() : '—';
                const trader = (e?.traderAddress || '').toString();
                const cfgId = (e?.configId || '').toString();
                const decision = (e?.decision || '').toString();
                const side = e?.trade?.side || e?.order?.side;
                const price = e?.trade?.price;
                const size = e?.trade?.size;
                const reason = e?.reason;
                const inferred = e?.trade?.inferred === true || (typeof e?.trade?.transactionHash === 'string' && e.trade.transactionHash.startsWith('posdelta:'));
                const q = e?.market?.question || e?.trade?.title || e?.market?.conditionId || e?.trade?.conditionId;

                return (
                  <div key={e.id || `${kind}-${createdAt}-${cfgId}`} className="p-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-dark-100 truncate">
                          {kind}
                          {inferred ? <span className="text-dark-400 font-normal"> • inferred</span> : null}
                          {decision ? <span className="text-dark-400 font-normal"> • {decision}</span> : null}
                        </p>
                        <p className="text-xs text-dark-500 mt-1 truncate">
                          {createdAt}
                          {trader ? ` • ${trader.slice(0, 10)}…${trader.slice(-6)}` : ''}
                          {cfgId ? ` • cfg ${cfgId.slice(0, 6)}…` : ''}
                        </p>
                        {kind === 'reconcile_diff' ? (
                          <div className="mt-2 text-xs text-dark-300">
                            <div>
                              localOpen {e?.counts?.localOpen ?? 0} • liveOpen {e?.counts?.liveOpen ?? 0} • liveOnly {e?.counts?.liveOnly ?? 0} • localOnly {e?.counts?.localOnly ?? 0}
                            </div>
                            {(e?.diff?.liveOnly?.length || 0) > 0 ? (
                              <div className="mt-1 text-dark-400">
                                liveOnly: {e.diff.liveOnly.slice(0, 6).join(', ')}
                              </div>
                            ) : null}
                            {(e?.diff?.localOnly?.length || 0) > 0 ? (
                              <div className="mt-1 text-dark-400">
                                localOnly: {e.diff.localOnly.slice(0, 6).join(', ')}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="text-xs text-dark-300 mt-2">
                            {q ? `${q} • ` : ''}
                            {side ? `${side} ` : ''}
                            {typeof size === 'number' ? `${size.toFixed(2)} ` : ''}
                            {typeof price === 'number' ? `@ $${price.toFixed(3)} ` : ''}
                            {reason ? `• ${reason}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detailed Trade Activity Log - Scrollable with verbose failure info */}
      <TradeActivityLog limit={200} />

      {/* Configs Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
        </div>
      ) : configs.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {configs.map(config => (
            <ConfigCard
              key={config._id}
              config={config}
              onToggle={handleToggle}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Settings className="w-12 h-12 mx-auto mb-4 text-dark-500" />
          <h3 className="text-lg font-medium mb-2">No Copy Configurations</h3>
          <p className="text-dark-400 max-w-md mx-auto mb-4">
            Create a copy configuration to start automatically copying trades from successful traders.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            Create Your First Config
          </button>
        </div>
      )}

      {/* Create Modal */}
      <CreateConfigModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setInitialTrader(null);
        }}
        initialTrader={initialTrader}
        subscriptionTier={subscriptionTier}
        onSubmit={handleCreate}
      />

      {/* Edit Modal */}
      <CreateConfigModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingConfig(null);
        }}
        editConfig={editingConfig}
        subscriptionTier={subscriptionTier}
        onSubmit={handleUpdate}
      />
    </div>
  );
}
